/**
 * SkipReasonMenu — portal menu for skipping/un-skipping a worklist case.
 *
 * Opened by right-clicking a worklist row or via the case-viewer's "Skip case"
 * button. When the case is not skipped it offers the preset reasons (and an
 * "Other…" item that reveals an optional free-text note); when it is already
 * skipped it offers "Unmark skipped". Reuses the reporter context-menu chrome
 * (useContextMenu + .chip-context-menu CSS) so positioning, dismissal, and
 * keyboard a11y match the chip menu.
 */

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { useContextMenu } from "./reporter/useContextMenu";
import { SKIP_REASON_LABEL, type SkipReason } from "../hooks/useSkippedStudies";

const MENU_WIDTH = 240;

// Preset reasons offered as one-click items; "other" is handled separately so
// it can reveal the optional note input.
const PRESET_REASONS: Exclude<SkipReason, "other">[] = ["separate_report", "wrong_comparison"];

export interface SkipReasonMenuProps {
  anchor: { x: number; y: number };
  isSkipped: boolean;
  /** Stored reason when already skipped — shown as a header label. */
  currentReason?: string;
  /** Stored free-text note (only set for the "other" reason) — shown so the
   * reviewer can see what they wrote. */
  currentNote?: string;
  onSkip: (reason: SkipReason, note?: string) => void;
  onUnskip: () => void;
  onClose: () => void;
}

export function SkipReasonMenu({
  anchor,
  isSkipped,
  currentReason,
  currentNote,
  onSkip,
  onUnskip,
  onClose,
}: SkipReasonMenuProps) {
  const { ref, style } = useContextMenu(anchor, onClose, MENU_WIDTH);
  const [noteMode, setNoteMode] = useState(false);
  const [note, setNote] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (noteMode) inputRef.current?.focus();
  }, [noteMode]);

  const submitOther = () => {
    onSkip("other", note.trim() || undefined);
    onClose();
  };

  return createPortal(
    <div
      ref={ref}
      className="chip-context-menu"
      role="menu"
      style={style}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {isSkipped ? (
        <>
          <div className="px-2.5 py-1 text-[10px] uppercase tracking-wider text-fg-faint">
            Skipped
            {currentReason
              ? ` · ${(SKIP_REASON_LABEL as Record<string, string>)[currentReason] ?? currentReason}`
              : ""}
          </div>
          {currentNote && (
            <div className="px-2.5 pb-1 text-[11px] italic text-fg-dim normal-case wrap-break-word">
              “{currentNote}”
            </div>
          )}
          <button
            role="menuitem"
            type="button"
            className="chip-context-menu-item"
            onClick={() => {
              onUnskip();
              onClose();
            }}
          >
            <span>Unmark skipped</span>
          </button>
        </>
      ) : noteMode ? (
        <div className="px-2 py-1.5 flex flex-col gap-1.5">
          <input
            ref={inputRef}
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onKeyDown={(e) => {
              // Keep arrows/Enter from reaching the menu's document-level roving
              // handler while the reader types; Enter submits, Escape cancels.
              e.stopPropagation();
              if (e.key === "Enter") {
                e.preventDefault();
                submitOther();
              } else if (e.key === "Escape") {
                e.preventDefault();
                onClose();
              }
            }}
            placeholder="Optional note…"
            className="w-full px-2 py-1 text-xs bg-bg border border-border-strong rounded-xs text-fg placeholder-fg-faint focus:outline-none focus:border-primary/50"
          />
          <button
            type="button"
            onClick={submitOther}
            className="self-end text-[11px] font-semibold px-2.5 py-1 rounded-xs bg-signal text-fg hover:bg-signal/90 transition-colors"
          >
            Skip case
          </button>
        </div>
      ) : (
        <>
          <div className="px-2.5 py-1 text-[10px] uppercase tracking-wider text-fg-faint">
            Mark skipped — reason
          </div>
          {PRESET_REASONS.map((reason) => (
            <button
              key={reason}
              role="menuitem"
              type="button"
              className="chip-context-menu-item"
              onClick={() => {
                onSkip(reason);
                onClose();
              }}
            >
              <span>{SKIP_REASON_LABEL[reason]}</span>
            </button>
          ))}
          <button
            role="menuitem"
            type="button"
            className="chip-context-menu-item"
            onClick={() => setNoteMode(true)}
          >
            <span>Other…</span>
          </button>
        </>
      )}
    </div>,
    document.body,
  );
}
